import {
  vehicleTypeColors,
  gradientColors,
  movementColors,
  system,
} from "@/styles/theme";
import { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";

export const DEFAULT_COLOUR: RgbColour = hexToRgb(
  system.token("colors.grey.400"),
);

export type RgbColour = [number, number, number];

export interface ColourRange {
  min: { rgb: RgbColour; value: number };
  mid: { rgb: RgbColour; value: number };
  max: { rgb: RgbColour; value: number };
}

export function hexToRgb(hex: string): RgbColour {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

export const vehicleTypeRgb: Record<TrajectoryPointDtoVehicleType, RgbColour> =
  Object.fromEntries(
    Object.entries(vehicleTypeColors).map(([key, hex]) => [key, hexToRgb(hex)]),
  ) as Record<TrajectoryPointDtoVehicleType, RgbColour>;

export const gradientRgb = {
  speed: {
    min: hexToRgb(gradientColors.speed.min),
    mid: hexToRgb(gradientColors.speed.mid),
    max: hexToRgb(gradientColors.speed.max),
  },
  acceleration: {
    min: hexToRgb(gradientColors.acceleration.min),
    mid: hexToRgb(gradientColors.acceleration.mid),
    max: hexToRgb(gradientColors.acceleration.max),
  },
  density: {
    min: hexToRgb(gradientColors.density.min),
    mid: hexToRgb(gradientColors.density.mid),
    max: hexToRgb(gradientColors.density.max),
  },
} as const;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function interpolate(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function interpolateColour(
  value: number,
  colourRange: ColourRange,
): RgbColour {
  const { min, mid, max } = colourRange;

  if (value <= mid.value) {
    // Interpolate between min and mid
    const t = clamp((value - min.value) / (mid.value - min.value), 0, 1);
    const r = Math.round(interpolate(min.rgb[0], mid.rgb[0], t));
    const g = Math.round(interpolate(min.rgb[1], mid.rgb[1], t));
    const b = Math.round(interpolate(min.rgb[2], mid.rgb[2], t));
    return [r, g, b];
  } else {
    // Interpolate between mid and max
    const t = clamp((value - mid.value) / (max.value - mid.value), 0, 1);
    const r = Math.round(interpolate(mid.rgb[0], max.rgb[0], t));
    const g = Math.round(interpolate(mid.rgb[1], max.rgb[1], t));
    const b = Math.round(interpolate(mid.rgb[2], max.rgb[2], t));
    return [r, g, b];
  }
}

export function getVehicleTypeColour(
  vehicleType: TrajectoryPointDtoVehicleType,
): RgbColour {
  return vehicleTypeRgb[vehicleType];
}

export const buildMovementColorMap = (
  movementIds: Iterable<number>,
): Map<number, string> => {
  return new Map(
    [...movementIds].map((id) => [
      id,
      movementColors[id % movementColors.length],
    ]),
  );
};
